import useOverlay from "@/hooks/common/useOverlay";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import TravelInfoPopup from "@COMPONENTS/product/common/popup/desktop/TravelInfoPopup";
import * as style from "@COMPONENTS/style/detailpackage/detailitinerary";
import * as itinerary from "@COMPONENTS/style/detailpackage/itinerarytemplate";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {EditorBox} from "@FRAMEWORK/ui/desktop/editor";
import {NoteBox} from "@FRAMEWORK/ui/desktop/layout";
import {BlueText, SubTitle} from "@FRAMEWORK/ui/desktop/text";
import useEventScheduleFlight, {EventScheduleFlight} from "@QUERIES/prdt/event/useEventScheduleFlight";
import useEventScheduleTrip, {EventScheduleDay, EventScheduleTime} from "@QUERIES/prdt/event/useEventScheduleTrip";
import useEventTourDetail, {EventTourDetail} from "@QUERIES/prdt/event/useEventTourDetail";
import dayjs from "dayjs";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import ReactPlayer from "react-player";
import {TourDetailTemplate} from "./TemplateTabs";

export interface DayTimes extends EventScheduleTime {
	tourDetails?: EventTourDetail[];
}

export interface Days extends EventScheduleDay {
	dayTimes?: DayTimes[];
	dayLocationInfo?: string;
}

const daykor = ["일", "월", "화", "수", "목", "금", "토"];

const DepartDayComponent = (day: Days & {filght: EventScheduleFlight[]}) => {
	const {meetAirDivi, meetAirTm, meetAirPlace, meetAirTelNo, meetAirImgUrl, meetAirNote} = day.dayTimes[0];
	const {outDeprtCityNm, outDeprtTm, outArrvTm, outDayDiff, trTp, dayNo, evStartDt, outArrvCityNm} = day.dayTimes[0];
	const tourDate = dayjs(evStartDt).add(dayNo - 1, "day");
	const arriveDate = tourDate.add(outDayDiff, "day");
	if (trTp !== "A") return <></>;

	const filghtFilter = day.filght?.filter(v => v.immigDivi === "OD"); // 출국 필터

	return (
		<>
			<style.FlightTimeBox>
				<style.Departure>
					<style.FlightCountry>{`${outDeprtCityNm || ""} 출발`}</style.FlightCountry>
					<style.FlightTime>{outDeprtTm?.replace(/(\d{2})(\d{2})/g, "$1:$2")}</style.FlightTime>
					<style.FlightDate>
						{tourDate.format("YYYY.MM.DD")}({daykor[tourDate.day()]})
					</style.FlightDate>
				</style.Departure>
				{filghtFilter?.length > 0 ? (
					<style.StopoverIconBox>
						<style.StopoverIcon>경유 {filghtFilter.length}회</style.StopoverIcon>
						<span></span>
						<style.TransitWrap>
							{filghtFilter.map((item, index) => (
								<style.TransitText key={index}>{item.cityKorNm}</style.TransitText>
							))}
						</style.TransitWrap>
					</style.StopoverIconBox>
				) : (
					<style.FlightIconBox>
						<span></span>
					</style.FlightIconBox>
				)}
				<style.Arrival>
					<style.FlightCountry>{`${outArrvCityNm || ""} 도착`}</style.FlightCountry>
					<style.FlightTime>{outArrvTm?.replace(/(\d{2})(\d{2})/g, "$1:$2")}</style.FlightTime>
					<style.FlightDate>
						{arriveDate.format("YYYY.MM.DD")}({daykor[arriveDate.day()]})
						{outDayDiff > 0 && <style.AddDate>{`+${outDayDiff}일`}</style.AddDate>}
					</style.FlightDate>
				</style.Arrival>
			</style.FlightTimeBox>
			{meetAirDivi !== "M0000" && (
				<style.MeetingInfoBox>
					<h4>출발 전 공항미팅안내</h4>
					<style.MeetingList>
						{meetAirTm && (
							<dl>
								<dt>미팅 일시</dt>
								<dd>{meetAirTm}</dd>
							</dl>
						)}
						{meetAirPlace && (
							<dl>
								<dt>미팅 장소</dt>
								<dd dangerouslySetInnerHTML={{__html: meetAirPlace}} />
							</dl>
						)}
						{meetAirTelNo && (
							<dl>
								<dt>담당자 연락처</dt>
								<dd>{meetAirTelNo}</dd>
							</dl>
						)}
					</style.MeetingList>
					<style.MeetingEdior>
						<EditorBox>{meetAirNote && meetAirNote}</EditorBox>
						{meetAirImgUrl && <img src={`${publicRuntimeConfig.ENV_CIMG_CDN_URL}/${meetAirImgUrl}`} alt="미팅장소" />}
					</style.MeetingEdior>
				</style.MeetingInfoBox>
			)}
		</>
	);
};

/** 출발일(1일차) 추가 정보 */
const DepartDay = (props: Days & {filght: EventScheduleFlight[]}) => (
	<ErrorBoundary logMsg="DailySchedule DepartDay Error">
		<DepartDayComponent {...props} />
	</ErrorBoundary>
);

const ArriveDayComponent = (day: Days & {filght: EventScheduleFlight[]}) => {
	const {inDeprtCityNm, inArrvCityNm, inDeprtTm, inArrvTm, trTp, inDayDiff, evArriveDt} = day.dayTimes[0];
	if (trTp !== "A") return <></>;
	const arriveDate = dayjs(evArriveDt);
	const filghtFilter = day.filght?.filter(v => v.immigDivi === "IA"); // 입국 필터
	return (
		<>
			<style.FlightTimeBox>
				<style.Departure>
					<style.FlightCountry>{`${inDeprtCityNm || ""} 출발`}</style.FlightCountry>
					<style.FlightTime>{inDeprtTm?.replace(/(\d{2})(\d{2})/g, "$1:$2")}</style.FlightTime>
					<style.FlightDate>
						{arriveDate.add(-inDayDiff, "day").format("YYYY.MM.DD")}({daykor[arriveDate.add(-inDayDiff, "day").day()]})
					</style.FlightDate>
				</style.Departure>
				{filghtFilter?.length > 0 ? (
					<style.StopoverIconBox>
						<style.StopoverIcon>경유 {filghtFilter.length}회</style.StopoverIcon>
						<span></span>
						<style.TransitWrap>
							{filghtFilter.map((item, index) => (
								<style.TransitText key={index}>{item.cityKorNm}</style.TransitText>
							))}
						</style.TransitWrap>
					</style.StopoverIconBox>
				) : (
					<style.FlightIconBox>
						<span></span>
					</style.FlightIconBox>
				)}
				<style.Arrival>
					<style.FlightCountry>{`${inArrvCityNm || ""} 도착`}</style.FlightCountry>
					<style.FlightTime>{inArrvTm?.replace(/(\d{2})(\d{2})/g, "$1:$2")}</style.FlightTime>
					<style.FlightDate>
						{arriveDate.format("YYYY.MM.DD")}({daykor[arriveDate.day()]})
						{inDayDiff > 0 && <style.AddDate>{`+${inDayDiff}일`}</style.AddDate>}
					</style.FlightDate>
				</style.Arrival>
			</style.FlightTimeBox>
		</>
	);
};

/** 도착일 추가정보 */
const ArriveDay = (props: Days & {filght: EventScheduleFlight[]}) => (
	<ErrorBoundary logMsg="DailySchedule ArriveDay Error">
		<ArriveDayComponent {...props} />
	</ErrorBoundary>
);

const TimeScheduleComponent = ({times}: {times: DayTimes[]}) => {
	const [playing, setPlaying] = useState(false);

	return (
		<>
			{times.map((item, index) => {
				const {tmNm, tmTitle, tmContent, trvVimeoUrl, endInfoImgUrl, tourDetails, cityCd, cityNm, viCityNm} = item;

				return (
					<React.Fragment key={index}>
						{(index === 0 || (index > 0 && times[index - 1].cityCd !== cityCd)) && (
							<style.LocationTitle>
								<i></i>
								{cityNm}
								{viCityNm && <span>*{viCityNm}</span>}
							</style.LocationTitle>
						)}
						<style.LocationText>
							{tmNm && <span>{tmNm}</span>} {tmTitle}
						</style.LocationText>
						{tmContent && (
							<style.EditorBoxWrap>
								<EditorBox dangerouslySetInnerHTML={{__html: tmContent}} />
							</style.EditorBoxWrap>
						)}
						{/* imageTamplate */}
						{tourDetails.length > 0 && (
							<style.TemplateWrap>
								<itinerary.TemplateList>
									{tourDetails.map((detail, detailIndex) => (
										<TourDetailTemplate {...detail} key={detailIndex} />
									))}
								</itinerary.TemplateList>
							</style.TemplateWrap>
						)}
						{endInfoImgUrl && (
							<style.ImgBox>
								<img src={`${publicRuntimeConfig.ENV_CIMG_CDN_URL}${endInfoImgUrl}`} alt="이미지" />
							</style.ImgBox>
						)}
						{trvVimeoUrl && (
							<style.PlayBox>
								<ReactPlayer
									url={trvVimeoUrl}
									width="100%"
									height="100%"
									playing={playing}
									onPlay={() => setPlaying(true)}
									onPause={() => setPlaying(false)}
									volume={0}
									muted={true}
									controls
								/>
							</style.PlayBox>
						)}
					</React.Fragment>
				);
			})}
		</>
	);
};

const TimeSchedule = (props: {times: DayTimes[]}) => (
	<ErrorBoundary logMsg="DailySchedule TimeSchedule Error">
		<TimeScheduleComponent {...props} />
	</ErrorBoundary>
);

const DayInformationComponent = (day: Days) => {
	const [open] = useOverlay();
	const {accommYn, accommNote, accommNm, foodBYn, foodLYn, foodDYn, foodB, foodL, foodD, accommInfoId} = day;

	const openTravelInfo = async e => {
		e.preventDefault();

		// 숙소 데이터 세팅
		const data = {
			trvCd: "20",
			trvInfoId: accommInfoId,
			infoApiUrl: `/api/event-info/${accommInfoId}/residence`,
		};
		open(prop => <TravelInfoPopup {...prop} trv={data} />);
	};

	if (!(accommYn || accommNote || accommNm || foodBYn === "Y" || foodLYn === "Y" || foodDYn === "Y")) return <></>;
	const Lodgment = () => {
		if (accommYn === "Y") {
			return (
				<>
					<dd dangerouslySetInnerHTML={{__html: `-미정 ${accommNote?.replace(/\r\n/g, "<br/>") || ""}`}}></dd>
				</>
			);
		} else if (accommNm && accommYn !== "Y" && (accommInfoId || "").length > 0) {
			return (
				<dd>
					<a href="#" onClick={openTravelInfo}>
						{accommNm}
					</a>
					{accommNote && <span>{accommNote.replace(/\r\n/g, "<br/>")}</span>}
				</dd>
			);
		} else {
			if (!accommNm && !accommNote) return <dd>-</dd>;
			return (
				<dd>
					{accommNm && accommNm}
					{accommNote && <span>{accommNote.replace(/\r\n/g, "<br/>")}</span>}
				</dd>
			);
		}
	};
	return (
		<style.SummaryInfo>
			<dl>
				<dt>숙박</dt>
				<Lodgment />
			</dl>
			<dl>
				<dt>식사</dt>
				<dd>
					<style.MealList>
						<li>
							<BlueText>[조식]</BlueText> {foodB || "-"}
						</li>
						<li>
							<BlueText>[중식]</BlueText> {foodL || "-"}
						</li>
						<li>
							<BlueText>[석식]</BlueText> {foodD || "-"}
						</li>
					</style.MealList>
				</dd>
			</dl>
		</style.SummaryInfo>
	);
};

/** 숙박 식사 정보 */
const DayInformation = (props: Days) => (
	<ErrorBoundary logMsg="DailySchedule DayInformation Error">
		<DayInformationComponent {...props} />
	</ErrorBoundary>
);

const DayScheduleComponent = (day: Days & {lastDay: number; filght: EventScheduleFlight[]}) => {
	const {dayNo, evStartDt, dayLocationInfo, dayTimes, lastDay, filght} = day;
	const tourDate = dayjs(evStartDt).add(dayNo - 1, "day");

	return (
		<>
			<style.ScheduleTitle>
				<h3>{dayNo}일차</h3>
				<span>
					{tourDate.format("YYYY.MM.DD")}({daykor[tourDate.day()]}) - {dayLocationInfo}
				</span>
			</style.ScheduleTitle>
			<style.ScheduleContent>
				{/* 출국 */}
				{dayTimes.length > 0 && dayNo === 1 && <DepartDay {...day} filght={filght} />}
				<style.StepWrap>
					{/* 스케줄 */}
					<style.Location>{dayTimes.length > 0 && <TimeSchedule times={dayTimes} />}</style.Location>
				</style.StepWrap>
				{/* 입국 */}
				{dayTimes.length > 0 && dayNo === lastDay && <ArriveDay {...day} filght={filght} />}
				<DayInformation {...day} />
			</style.ScheduleContent>
		</>
	);
};

const DaySchedule = (props: Days & {lastDay: number; filght: EventScheduleFlight[]}) => (
	<ErrorBoundary logMsg="DailySchedule DaySchedule Error">
		<DayScheduleComponent {...props} />
	</ErrorBoundary>
);

const DailyScheduleComponent = ({evCd, isFixed}: {evCd: string; isFixed: boolean}) => {
	const goodsCd = evCd?.substring(0, 7);

	const scheduleTripFetch = useEventScheduleTrip(evCd, goodsCd);
	const tourDetailsFetch = useEventTourDetail(evCd);
	const eventScheduleFlight = useEventScheduleFlight(evCd); // 출입국 경유지 api
	const filght = eventScheduleFlight.data?.body;

	const days: Days[] = useMemo(() => {
		if (!scheduleTripFetch.isFetched || !tourDetailsFetch.isFetched) return null;
		const {
			scheduleDetail,
			scheduleDetailTm,
		}: {
			scheduleDetail: Array<Days>;
			scheduleDetailTm: Array<DayTimes>;
		} = scheduleTripFetch.data.body;
		const tourDetails = tourDetailsFetch.data?.body;
		return scheduleDetail.reduce((acc, detail) => {
			const dayTmList = scheduleDetailTm.filter(tm => {
				tm.tourDetails = tourDetails.filter(v => v.tmSeq === tm.tmSeq);
				return tm.dayNo === detail.dayNo;
			});
			detail.dayTimes = dayTmList;
			detail.dayLocationInfo = detail.dayTimes
				.reduce((cityNmAcc, cur) => {
					cityNmAcc.indexOf(cur.cityNm) === -1 && cityNmAcc.push(cur.cityNm);
					return cityNmAcc;
				}, [])
				.join("/");
			acc.push(detail);
			return acc;
		}, []);
	}, [scheduleTripFetch, tourDetailsFetch]);

	const [activeTab, setActiveTab] = useState(0);

	const ScrollToId = (id: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		const element = document.getElementById(id);
		if (element) element.scrollIntoView({behavior: "smooth"});
	};

	/* tab active */
	const handleScroll = useCallback(() => {
		const contentsElements = Array.from(document.querySelectorAll("[id^=dayContents]"));
		contentsElements.forEach((el, index) => {
			if (el && window.scrollY + 202 > window.scrollY + el.getBoundingClientRect().top) {
				setActiveTab(index);
			}
		});

		// last element 처리
		const scrollHeight = document.documentElement.scrollHeight;
		const scrollTop = document.documentElement.scrollTop;
		const clientHeight = document.documentElement.clientHeight;

		if (scrollTop + clientHeight >= scrollHeight) {
			setActiveTab(contentsElements.length - 1);
		}
	}, []);

	useEffect(() => {
		window.addEventListener("scroll", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	/* x scroll */
	const handleMovementTab = useCallback(() => {
		const _lastTabLeft = document.getElementById(`dayTab${days.length - 1}`).offsetLeft;
		const _containerWrapWidth = document.getElementById("__dayContainerWrap").clientWidth;
		// x스크롤 필요 없는 경우 return
		if (_containerWrapWidth > _lastTabLeft) return;

		const _activeTab = document.getElementById(`dayTab${activeTab}`);
		const _activeTabLeft = _activeTab.offsetLeft;
		const _activeTabWidth = _activeTab.offsetWidth;

		const container = document.getElementById("__dayContainer");
		const scrollLeft = _activeTabLeft - _containerWrapWidth + _activeTabWidth + 73;
		container.scrollLeft = scrollLeft;
	}, [activeTab]);

	useEffect(() => {
		const timer = setTimeout(() => handleMovementTab(), 500);
		return () => {
			clearTimeout(timer);
		};
	}, [activeTab]);

	return (
		<style.Wrap>
			<style.SubTitleWrap>
				<SubTitle>여행 일정</SubTitle>
			</style.SubTitleWrap>
			<NoteBox icon="point">
				<h4>참고하세요</h4>
				<p>
					여행 일정은 계약 체결 시 예상하지 못한 부득이한 사정 등이 발생하는 경우 여행자의 사전 동의를 거쳐 변경될 수
					있음을 양지하여 주시기 바랍니다.
				</p>
			</NoteBox>
			<style.DayList isFixed={isFixed} id="__dayContainerWrap">
				<ul id="__dayContainer">
					{days &&
						days.map((item, index) => (
							<li key={index} id={`dayTab${index}`}>
								<style.DayListItem
									onClick={ScrollToId(`dayContents${index}`)}
									isActive={activeTab === index}
									href={`dayContents${index}`}
								>
									{item.dayNo}일차
								</style.DayListItem>
							</li>
						))}
				</ul>
			</style.DayList>
			<style.ScheduleBox>
				{days &&
					days.map((item, index) => (
						<style.ScheduleIitem id={`dayContents${index}`} key={index}>
							<DaySchedule {...item} lastDay={days.length} filght={filght} />
						</style.ScheduleIitem>
					))}
			</style.ScheduleBox>
		</style.Wrap>
	);
};

// 일차별 일정
const DailySchedule = (props: {evCd: string; isFixed: boolean}) => (
	<ErrorBoundary logMsg="DailySchedule Error">
		<DailyScheduleComponent {...props} />
	</ErrorBoundary>
);

export default DailySchedule;
